<template>
    <div>

        <PageSpinner
            v-show="isPageLoading"
            :progress="progress"
            class="v-fade"
        />

        <div
            v-show="!isPageLoading"
            class="page-content v-fade"
        >
            <div class="row">
                <div class="col-lg-12">
                    <Card>
                        <template slot="title">
                            <span>Costs</span>
                            <a
                                v-if="isQuote"
                                :href="backToQuoteUrl"
                                class="badge badge-secondary ml-3"
                            ><i class="fa fa-chevron-left" /> Back to quote</a>
                        </template>
                        <template slot="actions">
                            <CostableTableActions />
                        </template>
                        <CostableTableFilters />
                        <CostableTableTabs v-if="areTabsEnabled" />
                        <CostableTable />
                    </Card>
                </div>
            </div>
            <div class="row">
                <div :class="[ hasAcceptedQuote ? 'col-lg-4' : 'col-lg-6' ]">
                    <CostableReportedInformation v-if="hasReportedInformation" />
                    <CostableCurrencies v-if="hasCurrencies" />
                </div>
                <div :class="[ hasAcceptedQuote ? 'col-lg-4' : 'col-lg-6' ]">
                    <CostableCostSummary v-if="hasCostsSummary" />
                    <CostableCostResourceTypes v-if="hasResourceTypeSummary" />
                </div>
                <div
                    v-if="hasAcceptedQuote"
                    class="col-lg-4"
                >
                    <CostableAcceptedQuote />
                </div>
            </div>
        </div>

        <CreateAccountModal />
        <CostableAddCostModal />
        <CostableAddGroupModal />
        <CostableAddPackageModal />
        <CostableAddToTabModal />
        <CostableAddContingencyModal v-if="canHaveContingency" />
        <CostableEditPricingModeModal v-if="hasCostsSummary || hasAcceptedQuote" />
        <CostableServiceChargeModal v-if="canHaveServiceCharge" />
        <CostableAddToGroupModal />
        <CostableResourcePickerModal />
        <CostableTableActionsExportSummaryPdfModal />
        <ConfirmationModal />
        <RenameModal />
        <CostableImportModal />
        <CostableColumnSettingsModal />

    </div>
</template>

<script>
import Card from '@/components/Card';
import ConfirmationModal from '@/components/ConfirmationModal';
import RenameModal from '@/components/RenameModal';
import CostableImportModal from '@/modules/costable/_components/CostableImportModal';
import CreateAccountModal from '@/components/CreateAccountModal';
import CostableColumnSettingsModal from '@/modules/costable/_components/CostableColumnSettingsModal';
import store from './_store';
import CostableTableActions from './_components/CostableTableActions';
import CostableTableTabs from './_components/CostableTableTabs';
import CostableTableFilters from './_components/CostableTableFilters';
import CostableTable from './_components/CostableTable';
import CostableReportedInformation from './_components/CostableReportedInformation';
import CostableCurrencies from './_components/CostableCurrencies';
import CostableCostSummary from './_components/CostableCostSummary';
import CostableCostResourceTypes from './_components/CostableCostResourceTypes';
import CostableAcceptedQuote from './_components/CostableAcceptedQuote';
import CostableAddCostModal from './_components/CostableAddCostModal';
import CostableAddGroupModal from './_components/CostableAddGroupModal';
import CostableAddPackageModal from './_components/CostableAddPackageModal';
import CostableAddToTabModal from './_components/CostableAddToTabModal';
import CostableAddContingencyModal from './_components/CostableAddContingencyModal';
import CostableEditPricingModeModal from './_components/CostableEditPricingModeModal';
import CostableAddToGroupModal from './_components/CostableAddToGroupModal';
import CostableResourcePickerModal from './_components/CostableResourcePickerModal';
import CostableTableActionsExportSummaryPdfModal from './_components/CostableTableActionsExportSummaryPdfModal';
import CostableServiceChargeModal from './_components/CostableServiceChargeModal';

export default {

    components: {
        CostableColumnSettingsModal,
        Card,
        CostableTableActions,
        CostableTableTabs,
        CostableTableFilters,
        CostableTable,
        CostableReportedInformation,
        CostableCurrencies,
        CostableCostSummary,
        CostableCostResourceTypes,
        CostableAcceptedQuote,
        CreateAccountModal,
        CostableAddCostModal,
        CostableAddGroupModal,
        CostableAddPackageModal,
        CostableAddToTabModal,
        CostableAddContingencyModal,
        CostableEditPricingModeModal,
        CostableAddToGroupModal,
        CostableResourcePickerModal,
        CostableTableActionsExportSummaryPdfModal,
        ConfirmationModal,
        RenameModal,
        CostableImportModal,
        CostableServiceChargeModal,
    },

    props: {
        costableType: {
            type: String,
            required: true,
        },
        costableId: {
            type: Number,
            required: true,
        },
    },

    computed: {
        isPageLoading() {
            return this.$store.getters['costable/isCostablePageLoading'];
        },
        canHaveContingency() {
            return this.$store.getters['costable/canHaveContingency'];
        },
        areTabsEnabled() {
            return this.$store.getters['costable/areTabsEnabled'];
        },
        hasReportedInformation() {
            return this.$store.getters['costable/hasReportedInformation'];
        },
        hasCostsSummary() {
            return this.$store.getters['costable/hasCostsSummary'];
        },
        hasResourceTypeSummary() {
            return this.$store.getters['costable/hasResourceTypeSummary'];
        },
        hasCurrencies() {
            return this.$store.getters['costable/hasCurrencies'];
        },
        hasAcceptedQuote() {
            return this.$store.getters['costable/hasAcceptedQuote'];
        },
        isQuote() {
            return this.$store.getters['costable/isQuote'];
        },
        backToQuoteUrl() {
            return window.location.href.substr(0, window.location.href.lastIndexOf('/'));
        },
        canHaveServiceCharge() {
            return this.$store.getters['costable/canHaveServiceCharge'];
        },
        progress() {
            return this.$store.getters['costable/progress'];
        },
    },

    created() {
        this.$store.registerModule('costable', store);
        this.$store.dispatch('costable/loadCostable', {
            type: this.costableType,
            id: this.costableId,
        });
        this.$store.dispatch('costable/loadSettings');
    },

};
</script>
